import React from "react"
import { useId } from "@reach/auto-id"
import { isNotEmpty, isDefined } from "utils/validation"
import { useTranslation } from "react-i18next"

import { handleInternalInputValidation } from "../formUtils"
import { Errors } from "../Errors"

import "./InputRadio.scoped.scss"

export const InputRadio = ({
	// Attributes
	id,
	value,
	options,
	name,
	label = "",
	disabled,
	readonly = false,
	// Styles
	style,
	className = "",
	classNameLabel = "",
	classNameOptionsContainer = "",
	classNameOption = "",
	classNameOptionLabel = "",
	// Error management
	required = false,
	customRules,
	showErrors,
	// Handler
	onChange,
	onValidate,
}) => {
	const [errors, setErrors] = React.useState([])
	const { t } = useTranslation()

	const prefix = useId()
	const inputId = id ?? `${prefix}-${name}`
	const errorId = `${inputId}-error`

	const rules = React.useMemo(
		() => [
			{
				key: "radio-empty",
				check: (value) => (required ? isNotEmpty(value) : true),
				message: t("error_mandatory_field", { ns: "form" }),
			},
			...(customRules || []),
		],
		[required, t]
	) // eslint-disable-line
	// customRules in dependencies array trigger an infinite loop,
	// because custom rules include a check property defining a new function,
	// preventing useMemo to return a valid shallow-comparison
	// or event useDeepMemo to return a valid deep-comparison

	const validate = React.useCallback(
		(currentValue) =>
			handleInternalInputValidation(currentValue, name, rules, setErrors, onValidate),
		[rules, name, onValidate]
	)

	// validate on mount and each time value change
	React.useEffect(() => {
		validate(value)
		return () => {
			// On unmounted erros and validation are no longer needed
			setErrors([])
			if (onValidate) onValidate(name, [])
		}
	}, [value, validate, onValidate, name])

	const handleChange = (event) => {
		const inputValue = {
			target: {
				name: name,
				value: event?.target?.value,
				label: event?.target?.value,
			},
		}

		if (event?.target && typeof options[0]?.value === "number") {
			inputValue.target.value = parseInt(event.target.value)
		}
		onChange(inputValue)
	}

	return (
		<>
			<div data-cy={`formfield-${name}`} className={className} style={style}>
				{label && (
					<label data-cy={`label-${name}`} className={"c-label mb-1 " + classNameLabel}>
						{label} {required && "*"}
					</label>
				)}
				<div className={"u-inline-flex " + classNameOptionsContainer}>
					{options &&
						options.map((option, index) => {
							return (
								<label
									data-cy={`input-label-${name}-${index}`}
									htmlFor={option.id}
									className={
										"c-radio mb-2 " +
										(classNameOption || "") +
										(value ===
										(isDefined(option.value) ? option.value : option.label)
											? " is-checked"
											: "") +
										(disabled || readonly ? " is-disabled" : "")
									}
									key={option.id || index}
								>
									<input
										data-cy={`input-${name}-${index}`}
										className="u-sr-only"
										type="radio"
										value={
											isDefined(option.value) ? option.value : option.label
										}
										name={name}
										id={option.id}
										checked={
											value ===
											(isDefined(option.value) ? option.value : option.label)
										}
										disabled={disabled}
										readOnly={readonly}
										required={required}
										onChange={(e) => !readonly && handleChange(e)}
										onBlur={() => validate(value)}
									/>
									<span className={classNameOptionLabel}>
										{t(`${option.label}`)}
									</span>
								</label>
							)
						})}
				</div>
				{/* Error messages */}
				{showErrors && <Errors dataCy={`error-${name}`} id={errorId} errors={errors} />}
			</div>
		</>
	)
}
